import { useEffect, useRef, useState, MutableRefObject } from 'react'

interface MarkerPos {
  address?: string
  coordinates?: google.maps.GeocoderGeometry['location']
}

interface MapsHookResult {
  map: google.maps.Map
  marker: google.maps.Marker
  mapRef: MutableRefObject<HTMLDivElement>
  setMarkerPosition: (opts: MarkerPos) => void
}

export default function useGoogleMaps(
  options: google.maps.MapOptions
): MapsHookResult {
  const [map, setMap] = useState(null)
  const [marker, setMarker] = useState(null)
  const mapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!globalThis?.google || !mapRef?.current || map) {
      return
    }

    const createdMap = new google.maps.Map(mapRef.current, options)

    setMap(createdMap)
    setMarker(
      new google.maps.Marker({
        map: createdMap,
        anchorPoint: new google.maps.Point(39, -97)
      })
    )
  }, [mapRef, options, map])

  const setMarkerPosition = (options: MarkerPos) => {
    if (!globalThis.google || !mapRef.current || !map) {
      return
    }

    /**
     * When the cordinates are passed it means that the address was already
     * geo-coded servder side. No need to do this again on the browser.
     */
    if (options?.coordinates) {
      const { lat, lng } = options.coordinates

      marker.setVisible(true)
      marker.setPosition({ lat, lng })
      map.setZoom(12)
      return map.setCenter({ lat, lng })
    }

    const geocoder = new google.maps.Geocoder()

    geocoder?.geocode({ address: options.address }, function (results, status) {
      if (status == google.maps.GeocoderStatus.OK) {
        const lat = results?.[0]?.geometry?.location?.lat()
        const lng = results?.[0]?.geometry?.location?.lng()

        marker.setVisible(true)
        marker.setPosition({ lat, lng })
        map.setZoom(12)
        map.setCenter({ lat, lng })
      } else {
        map.setZoom(3)
        map.setCenter({ lat: 39, lng: -97 })
        marker.setVisible(false)
      }
    })
  }

  return {
    map,
    marker,
    mapRef,
    setMarkerPosition
  }
}
